import { AppBar, Avatar, Box, Button, Dialog, DialogContent, IconButton, Stack, Toolbar, Typography, alpha, useMediaQuery, useTheme } from "@mui/material";
import Grid from '@mui/material/Grid2';
import React, { useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import loadable from "@loadable/component";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import LotterySelector from "./lotterySelector";
import { GameContext, getLotteryRoutePath, isAuthenticatedUser, LoginContext, resolveLotteryIdFromRouteSlug, UserContext } from 'Components/Context';
import RandoStatLogo from "Components/Logo";
import LNG_MAP from "i18n/languages";
import { COUNTRY_LOTTERY_MAP, getCountryOption, normalizeCountryCode } from "./countryLotteryConfig";

const MainMenu = loadable(() => import("./mainMenu"));
const SignIn = loadable(() => import("Components/Auth"));

const Header = () => {
  const { i18n, t } = useTranslation("common")
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.only("xs"), { noSsr: true });
  const isSmScreen = useMediaQuery(theme.breakpoints.only("sm"), { noSsr: true });
  const navigate = useNavigate()
  const location = useLocation();
  const uContext = useContext(UserContext);
  const { triggerLogin, setTriggerLogin } = useContext(LoginContext);
  const { selectedLotteryId, setSelectedLotteryId, supportedLotteries } = useContext(GameContext);
  const isAuthenticated = isAuthenticatedUser(uContext.user);
  const displayName = uContext.user?.name || uContext.user?.email || t("header.mainMenu.userFallback");
  const avatarSrc = uContext.user?.picture || undefined;
  const dismissedMismatchKeysRef = React.useRef(new Set());
  const [lotteryMismatchPrompt, setLotteryMismatchPrompt] = React.useState(null);
  const showLotterySelector = useMemo(() => {
    const segments = (location.pathname || "").split("/").filter(Boolean);
    const locale = segments[0]?.toLowerCase();
    const lotterySlug = segments[1];

    if (!Object.prototype.hasOwnProperty.call(LNG_MAP, locale)) {
      return false;
    }

    return Boolean(resolveLotteryIdFromRouteSlug(lotterySlug, ""));
  }, [location.pathname]);

  const navigateHome = () => {
    navigate(`/${i18n.language}`);
    // const locale = (i18n.language || "en").split("-")[0];
    // navigate(getLotteryRoutePath(locale, selectedLotteryId));
  }

  const [isAccountDrawerOpen, setIsAccountDrawerOpen] = React.useState(false);
  const [isStandaloneLoginOpen, setIsStandaloneLoginOpen] = React.useState(false);

  useEffect(() => {
    if (!triggerLogin) {
      return;
    }
    setIsStandaloneLoginOpen(true);
    setTriggerLogin(false);
  }, [setTriggerLogin, triggerLogin]);

  useEffect(() => {
    if (isAuthenticated && isStandaloneLoginOpen) {
      setIsStandaloneLoginOpen(false);
    }
  }, [isAuthenticated, isStandaloneLoginOpen]);

  const resolveLocaleFromPath = () => {
    const localeFromPath = location.pathname.split("/")[1]?.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(LNG_MAP, localeFromPath)) {
      return localeFromPath;
    }
    return (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  };

  const evaluateCountryLotteryMismatch = React.useCallback((countryCode, lotteryId) => {
    const normalizedCountryCode = normalizeCountryCode(countryCode);
    if (!normalizedCountryCode || !lotteryId) {
      setLotteryMismatchPrompt(null);
      return;
    }

    const availableLotteryIds = (COUNTRY_LOTTERY_MAP[normalizedCountryCode] || []);
    if (availableLotteryIds.length === 0 || availableLotteryIds.includes(lotteryId)) {
      setLotteryMismatchPrompt(null);
      return;
    }

    const supportedLotteryIds = new Set((supportedLotteries || []).map((lottery) => lottery.id));
    const suggestedLotteryId = availableLotteryIds.find((item) => supportedLotteryIds.has(item));
    if (!suggestedLotteryId) {
      setLotteryMismatchPrompt(null);
      return;
    }

    const mismatchKey = `${normalizedCountryCode}|${lotteryId}`;
    if (dismissedMismatchKeysRef.current.has(mismatchKey)) {
      setLotteryMismatchPrompt(null);
      return;
    }

    setLotteryMismatchPrompt({
      mismatchKey,
      currentLotteryId: lotteryId,
      suggestedLotteryId,
      countryCode: normalizedCountryCode,
    });
  }, [supportedLotteries]);

  const handleDrawerSessionClose = React.useCallback((payload) => {
    const changedCountry = payload?.changedPreferences?.country;
    if (!changedCountry) {
      return;
    }

    evaluateCountryLotteryMismatch(changedCountry, selectedLotteryId);
  }, [evaluateCountryLotteryMismatch, selectedLotteryId]);

  const handleSwitchLotteryFromPrompt = () => {
    if (!lotteryMismatchPrompt) {
      return;
    }
    const locale = resolveLocaleFromPath();
    setSelectedLotteryId(lotteryMismatchPrompt.suggestedLotteryId, { explicit: true });
    navigate(getLotteryRoutePath(locale, lotteryMismatchPrompt.suggestedLotteryId));
    setLotteryMismatchPrompt(null);
  };

  const handleDismissLotteryPrompt = () => {
    if (!lotteryMismatchPrompt) {
      return;
    }
    dismissedMismatchKeysRef.current.add(lotteryMismatchPrompt.mismatchKey);
    setLotteryMismatchPrompt(null);
  };

  React.useEffect(() => {
    if (!lotteryMismatchPrompt) {
      return;
    }
    if (lotteryMismatchPrompt.currentLotteryId !== selectedLotteryId) {
      setLotteryMismatchPrompt(null);
    }
  }, [lotteryMismatchPrompt, selectedLotteryId]);

  const resolveLotteryLabel = React.useCallback((lotteryId) => {
    const lottery = (supportedLotteries || []).find((item) => item.id === lotteryId);
    return t(lottery?.labelKey || `lotteries.${lotteryId}`, {
      defaultValue: lottery?.name || lotteryId,
    });
  }, [supportedLotteries, t]);

  const suggestedLotteryLabel = lotteryMismatchPrompt
    ? resolveLotteryLabel(lotteryMismatchPrompt.suggestedLotteryId)
    : "";
  const mismatchCountryLabel = lotteryMismatchPrompt
    ? t(getCountryOption(lotteryMismatchPrompt.countryCode)?.labelKey || "header.mainMenu.chooseCountry")
    : "";
  const currentLotteryLabel = lotteryMismatchPrompt
    ? resolveLotteryLabel(lotteryMismatchPrompt.currentLotteryId)
    : "";

  const switchLotteryPrompt = () => {
    return (
      <Grid
        justifyContent={"center"}
        alignItems={"center"}
        display={"flex"}
        size={{ xs: 12, md: "auto" }}
      >
        <Box
          sx={{
            width: { xs: "100%", sm: "auto" },
            height: "fit-content",
            py: { xs: 1, md: 0 },
            display: "flex",
            justifyContent: "center",
            // flexGrow: 1
          }}
        >
          <Box
            sx={(themeValue) => ({
              width: "100%",
              maxWidth: "min(100%, 960px)",
              borderRadius: 2,
              px: { xs: 1.25, sm: 2 },
              py: { xs: 1, sm: 1, md: 0 },
              display: { xs: "flex", sm: "flex" },
              alignItems: "center",
              gap: 1,
              border: `1px solid ${alpha(themeValue.palette.info.main, 0.26)}`,
              backgroundColor: alpha(themeValue.palette.info.main, 0.02),
            })}
          >
            <Stack id="stack"
              direction={{ xs: "column", sm: "row" }}
              justifyContent={"center"}
              alignItems={"center"}
              width={"99%"}
              gap={1}
            >
              <Typography variant="body2" sx={{ flex: 1 }}>
                {t("header.mainMenu.lotteryMismatchPrompt.message", {
                  defaultValue: "{{lottery}} is not available in {{country}}.",
                  lottery: currentLotteryLabel,
                  country: mismatchCountryLabel,
                })}
              </Typography>
              <Button
                size="medium"
                variant="contained"
                onClick={handleSwitchLotteryFromPrompt}
                sx={{
                  textTransform: "none", borderRadius: 999,
                  color: theme.palette.text.primary,
                  py: 0
                }}
              >
                {t("header.mainMenu.lotteryMismatchPrompt.switch", {
                  defaultValue: "Switch to {{lottery}}",
                  lottery: suggestedLotteryLabel,
                })}
              </Button>
            </Stack>

            <IconButton
              size="small"
              onClick={handleDismissLotteryPrompt}
              aria-label={t("header.mainMenu.lotteryMismatchPrompt.dismiss", {
                defaultValue: "Dismiss lottery suggestion",
              })}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Grid>
    )
  }
  return (
    <AppBar
      id="app-header-bar"
      position="relative"
      color="default"
      elevation={0}
      sx={{
        backgroundColor: "transparent",
        borderBottom: "2px dotted",
        borderRight: "none",
        borderTop: "none",
        // borderImage: "linear-gradient(90deg, rgba(60, 74, 83, 0.90), rgba(60, 74, 83, .00)) 1"
        borderImage: "linear-gradient(90deg, rgba(60, 74, 83, .00), rgba(60, 74, 83, 0.90)) 1"
        // borderBottomRightRadius: "10px", borderBottomLeftRadius: "10px"

      }}
    >
      <Toolbar
        variant="dense"
        id="app-bar"
        key="toolbar"
        sx={{
          display: "flex", flexGrow: 1, justifyContent: "left",
          minHeight: { xs: 56, sm: 56 },
        }}

      >
        <Grid container
          spacing={2}
          rowSpacing={0}
          justifyContent="center"
          flexGrow={1}
        >
          <Grid size="auto"
            onClick={navigateHome}
            sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
          >
            <RandoStatLogo showText={!isXsScreen} sx={{ ml: 0.0 }} />
          </Grid>
          <Grid
            size="auto"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
            }}
          >
            {showLotterySelector ? <LotterySelector /> : null}
          </Grid>
          {showLotterySelector && lotteryMismatchPrompt && !isXsScreen && !isSmScreen ? switchLotteryPrompt() : null}
          <Grid
            size="grow"
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <IconButton
              onClick={() => setIsAccountDrawerOpen((open) => !open)}
              size="large"
              color="inherit"
              aria-label={t("header.mainMenu.openAccountDrawer")}
              sx={{ p: isAuthenticated ? 1 : 1, borderRadius: "50%" }}
            >
              {isAuthenticated ? (
                <Avatar
                  src={avatarSrc}
                  alt={`${displayName} avatar`}
                  sx={{ width: 30, height: 30 }}
                >
                  {!avatarSrc ? displayName.charAt(0).toUpperCase() : null}
                </Avatar>
              ) : (
                <Avatar sx={{ width: 30, height: 30 }}>
                  <AccountCircleOutlinedIcon />
                </Avatar>
              )}
            </IconButton>
          </Grid>
          {showLotterySelector && lotteryMismatchPrompt && (isXsScreen || isSmScreen) ? switchLotteryPrompt() : null}
        </Grid>
      </Toolbar>

      {isAccountDrawerOpen ? (
        <MainMenu
          open={isAccountDrawerOpen}
          onClose={() => setIsAccountDrawerOpen(false)}
          onDrawerSessionClose={handleDrawerSessionClose}
        />
      ) : null}

      <Dialog
        open={isStandaloneLoginOpen}
        onClose={() => setIsStandaloneLoginOpen(false)}
        fullScreen={isXsScreen}
      >
        <DialogContent
          sx={{
            p: "0",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <SignIn />
        </DialogContent>
      </Dialog>
    </AppBar>
  );
}

export default Header;
